<script setup lang="ts">
import { ref, computed, reactive, watch, nextTick } from 'vue'
import { doc, updateDoc } from 'firebase/firestore'
import { db } from '@/firebase'
import draggable from 'vuedraggable'
import { usePersonnelStore } from '@/stores/personnel'
import { useTeamsStore } from '@/stores/teams'
import { PersonnelStatus, OverheadRole } from '@/types'
import type { Personnel, Team } from '@/types'
import FosDialog from '@/components/ui/FosDialog.vue'
import FosButton from '@/components/ui/FosButton.vue'
import PersonnelTooltip from '@/components/personnel/PersonnelTooltip.vue'
import { useDragState } from '@/composables/useDragState'

const props = defineProps<{
  incidentId: string
  opId: string
  team: Team
  searchQuery?: string
}>()

function isMatch(p: Personnel): boolean {
  const q = props.searchQuery?.trim().toLowerCase()
  if (!q) return false
  return (
    `${p.first} ${p.last}`.toLowerCase().includes(q) ||
    (p.agency ?? '').toLowerCase().includes(q)
  )
}

const emit = defineEmits<{
  'view-details': []
}>()

const personnelStore = usePersonnelStore()
const teamsStore = useTeamsStore()
const { startDrag, endDrag, isDragging } = useDragState()

function roleZoneClass(role: string) {
  const base = 'border border-dashed rounded-sm transition-colors'
  const isEmpty = (personnelByRole.value.get(role) ?? []).length === 0
  if (!isEmpty) return `${base} border-transparent`
  return isDragging.value
    ? `${base} border-primary/50 bg-primary/5`
    : `${base} border-transparent`
}

// ── Role zone definitions ──────────────────────────────────────────────────

const ROLE_ZONES = [
  { role: OverheadRole.IC, label: OverheadRole.IC, abbrev: 'IC', single: true, droppable: true },
  { role: 'Search Manager', label: 'Search Manager', abbrev: 'SEARCH MGR', single: true, droppable: true },
  { role: OverheadRole.OpsChief, label: OverheadRole.OpsChief, abbrev: 'OPS CHIEF', single: true, droppable: true },
  { role: 'Plans Section Chief', label: 'Plans Section Chief', abbrev: 'PLANS CHIEF', single: true, droppable: true },
  { role: 'Communications', label: 'Communications', abbrev: 'COMM', single: false, droppable: true },
  { role: 'Operations', label: 'Operations', abbrev: 'OPS', single: false, droppable: true },
  { role: 'Plans', label: 'Plans', abbrev: 'PLANS', single: false, droppable: true },
  { role: 'Logistics', label: 'Logistics', abbrev: 'LOG', single: false, droppable: true },
  { role: 'Family Liaison Officer', label: 'Family Liaison Officer', abbrev: 'FLO', single: false, droppable: true },
  { role: 'General Overhead', label: 'General Overhead', abbrev: 'GEN OH', single: false, droppable: true },
] as const

// ── Optimistic local role state ────────────────────────────────────────────
// Overrides Firestore data immediately on drag, cleared once Firestore confirms.
// Prevents vuedraggable from getting stale model-value arrays during the async write.

const localRoles = reactive(new Map<string, string>()) // personnel_id → role

// Once Firestore confirms a role change (userOps updates), remove the local override
watch(
  () => teamsStore.userOps.map((u) => `${u.personnel_id}:${u.role}:${u.active}`).join(','),
  () => {
    for (const [personnelId, pendingRole] of localRoles) {
      const uo = teamsStore.userOps.find(
        (u) => u.personnel_id === personnelId && u.team_id === props.team.team_id && u.active,
      )
      if (!uo || uo.role === pendingRole) {
        localRoles.delete(personnelId)
      }
    }
  },
)

// ── Personnel grouped by role ──────────────────────────────────────────────

const personnelByRole = computed(() => {
  // Build role → Set<personnel_id> from Firestore userOps.
  const firestoreRoleMap = new Map<string, Set<string>>()
  for (const zone of ROLE_ZONES) firestoreRoleMap.set(zone.role, new Set())

  for (const uo of teamsStore.userOps) {
    if (uo.team_id !== props.team.team_id || !uo.active) continue
    const role = uo.role || 'General Overhead'
    if (!firestoreRoleMap.has(role)) firestoreRoleMap.set(role, new Set())
    firestoreRoleMap.get(role)!.add(uo.personnel_id)
  }

  // Build result map with optimistic overrides applied
  const result = new Map<string, Personnel[]>()
  for (const zone of ROLE_ZONES) result.set(zone.role, [])

  for (const p of personnelStore.roster) {
    if (p.time_out !== null || p.team_id !== props.team.team_id) continue

    let role: string = 'General Overhead'

    if (localRoles.has(p.personnel_id)) {
      // Optimistic: already dragged, Firestore hasn't confirmed yet
      role = localRoles.get(p.personnel_id)!
    } else {
      // Look up by personnel_id only — never fall back to user_id, which is ambiguous
      // when multiple personnel records share the same Firebase account
      let found = false
      for (const zone of ROLE_ZONES) {
        if (zone.role === 'General Overhead') continue
        if (firestoreRoleMap.get(zone.role)?.has(p.personnel_id)) {
          role = zone.role
          found = true
          break
        }
      }
      if (!found) role = 'General Overhead'
    }

    const list = result.get(role)
    if (list) {
      list.push(p)
    } else {
      result.get('General Overhead')?.push(p)
    }
  }

  return result
})

const checkedOutMembers = computed(() =>
  personnelStore.roster.filter((p) => p.time_out !== null && p.team_id === props.team.team_id),
)

const memberCount = computed(() =>
  personnelStore.roster.filter((p) => p.team_id === props.team.team_id).length,
)

// ── IC confirmation state ─────────────────────────────────────────────────

const pendingIcPerson = ref<Personnel | null>(null)
const pendingExistingIcs = ref<Personnel[]>([])
const showIcConfirm = ref(false)

async function confirmIcAssignment() {
  const person = pendingIcPerson.value
  if (!person) return

  showIcConfirm.value = false

  // Unassign existing ICs first
  for (const oldIc of pendingExistingIcs.value) {
    localRoles.delete(oldIc.personnel_id)
    await doUnassignFromTeam(oldIc)
  }

  // Assign new IC
  await doAssignToTeam(person)
  await teamsStore.setMemberRole(
    props.incidentId,
    props.opId,
    props.team.team_id,
    person.user_id,
    OverheadRole.IC,
    person.personnel_id,
  )

  pendingIcPerson.value = null
  pendingExistingIcs.value = []
}

function cancelIcAssignment() {
  if (pendingIcPerson.value) {
    localRoles.delete(pendingIcPerson.value.personnel_id)
  }
  showIcConfirm.value = false
  pendingIcPerson.value = null
  pendingExistingIcs.value = []
}

// ── Core helpers ───────────────────────────────────────────────────────────

async function doUnassignFromTeam(person: Personnel) {
  await teamsStore.unassignMemberFromTeam(
    props.incidentId,
    props.opId,
    person.user_id,
    person.personnel_id,
  )
  await updateDoc(
    doc(
      db,
      'incidents',
      props.incidentId,
      'operational_periods',
      props.opId,
      'personnel',
      person.personnel_id,
    ),
    { team_id: null, status: PersonnelStatus.Available },
  )
}

async function doAssignToTeam(person: Personnel) {
  await teamsStore.assignMemberToTeam(
    props.incidentId,
    props.opId,
    person.user_id,
    props.team.team_id,
    person.personnel_id,
  )
  await updateDoc(
    doc(
      db,
      'incidents',
      props.incidentId,
      'operational_periods',
      props.opId,
      'personnel',
      person.personnel_id,
    ),
    { team_id: props.team.team_id, status: PersonnelStatus.Assigned },
  )
}

// ── Role zone drag handlers ────────────────────────────────────────────────

async function handleRoleZoneChange(
  event: { added?: { element: Personnel } },
  role: string,
) {
  if (!event.added) return
  const person = event.added.element
  if (person.status === PersonnelStatus.OutOfService) return

  if (role === OverheadRole.IC) {
    // Capture existing ICs before applying optimistic state
    const existingIcs = (personnelByRole.value.get(OverheadRole.IC) ?? []).filter(
      (p) => p.personnel_id !== person.personnel_id,
    )
    pendingIcPerson.value = person
    pendingExistingIcs.value = existingIcs
    // Apply optimistic state so person stays in IC zone if already on this team
    localRoles.set(person.personnel_id, OverheadRole.IC)
    showIcConfirm.value = true
    return
  }

  // For other single-occupant zones, displace any existing occupant to General Overhead
  const zoneConfig = ROLE_ZONES.find((z) => z.role === role)
  if (zoneConfig?.single) {
    const displaced = (personnelByRole.value.get(role) ?? []).filter(
      (p) => p.personnel_id !== person.personnel_id,
    )
    for (const p of displaced) {
      localRoles.set(p.personnel_id, 'General Overhead')
      await teamsStore.setMemberRole(
        props.incidentId,
        props.opId,
        props.team.team_id,
        p.user_id,
        'General Overhead',
        p.personnel_id,
      )
    }
  }

  // Apply optimistic local state immediately so vuedraggable gets correct model-values
  localRoles.set(person.personnel_id, role)

  // Always ensure this person has their own user_op record keyed by personnel_id.
  // This also deactivates any legacy records that were keyed only by user_id,
  // which prevents multiple people sharing the same user_id from bleeding into each other's role.
  await doAssignToTeam(person)

  // Persist role to Firestore — keyed by personnel_id, so works even when user_id is null
  await teamsStore.setMemberRole(
    props.incidentId,
    props.opId,
    props.team.team_id,
    person.user_id,
    role,
    person.personnel_id,
  )
}

// ── Identifier inline edit ─────────────────────────────────────────────────

const collapsed = ref(false)

const editingId = ref(false)
const localIdentifier = ref(props.team.team_identifier)
const inputRef = ref<HTMLInputElement | null>(null)

function startEditing() {
  localIdentifier.value = props.team.team_identifier
  editingId.value = true
  nextTick(() => inputRef.value?.focus())
}

function saveIdentifier() {
  editingId.value = false
  const trimmed = localIdentifier.value.trim()
  if (trimmed && trimmed !== props.team.team_identifier) {
    teamsStore.updateTeamIdentifier(props.incidentId, props.opId, props.team.team_id, trimmed)
  } else {
    localIdentifier.value = props.team.team_identifier
  }
}

function cancelEditing() {
  editingId.value = false
  localIdentifier.value = props.team.team_identifier
}
</script>

<template>
  <div class="@container border border-border bg-surface rounded-sm cursor-pointer col-span-full" @click="emit('view-details')">
    <!-- Header -->
    <div class="flex items-center gap-1.5 px-2.5 pt-2 pb-2" @click.stop>
      <input
        v-if="editingId"
        ref="inputRef"
        v-model="localIdentifier"
        class="font-mono font-bold text-base w-20 border border-orange-500 rounded px-1 py-0 outline-none focus:ring-1 focus:ring-orange-400 bg-surface-container-high text-on-surface"
        @blur="saveIdentifier"
        @keydown.enter="saveIdentifier"
        @keydown.escape="cancelEditing"
      />
      <span
        v-else
        class="font-black font-headline text-base uppercase tracking-tight text-on-surface cursor-text hover:text-primary shrink-0"
        @click.stop="startEditing"
      >
        {{ team.team_identifier }}
      </span>

      <span class="text-[10px] font-mono text-outline-variant ml-1">{{ memberCount }} mbr</span>

      <button
        class="ml-auto text-outline hover:text-on-surface-variant transition-colors"
        :title="collapsed ? 'Expand' : 'Collapse'"
        @click.stop="collapsed = !collapsed"
      >
        <span class="material-symbols-outlined text-[18px]">{{ collapsed ? 'expand_more' : 'expand_less' }}</span>
      </button>
    </div>

    <!-- Role zones grid -->
    <div v-show="!collapsed" class="border-t border-border px-2.5 pt-2 pb-2" @click.stop>
      <div class="grid grid-cols-2 @md:grid-cols-3 @lg:grid-cols-4 gap-2">
        <div
          v-for="zone in ROLE_ZONES"
          :key="zone.role"
          :class="['flex flex-col gap-1', zone.role === 'General Overhead' && 'col-span-2 @md:col-span-3 @lg:col-span-4']"
        >
          <p class="text-[10px] font-mono text-outline uppercase tracking-widest mb-1">
            <span class="@[480px]:hidden">{{ zone.abbrev }}</span>
            <span class="hidden @[480px]:inline">{{ zone.label }}</span>
          </p>

          <div :class="roleZoneClass(zone.role)" class="relative">
            <draggable
              :model-value="personnelByRole.get(zone.role) ?? []"
              :group="{ name: 'personnel' }"
              item-key="personnel_id"
              ghost-class="drag-ghost--chip"
              drag-class="drag-cursor--chip"
              :force-fallback="true"
              :fallback-on-body="true"
              class="flex flex-wrap gap-1 min-h-[38px] p-1.5"
              @start="startDrag"
              @end="endDrag"
              @change="(e: { added?: { element: Personnel } }) => handleRoleZoneChange(e, zone.role)"
            >
              <template #item="{ element }">
                <div
                  :data-person-name="`${element.last}, ${element.first}`"
                  :class="isMatch(element)
                    ? 'flex items-center px-2 py-0.5 text-xs bg-primary/15 border border-primary/70 text-primary cursor-grab select-none rounded-sm'
                    : zone.role === OverheadRole.IC
                      ? 'inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-primary/10 border border-primary/30 cursor-grab select-none rounded-sm'
                      : 'flex items-center px-2 py-0.5 text-xs bg-surface-container border border-border cursor-grab select-none hover:border-outline-variant rounded-sm'"
                >
                  <PersonnelTooltip :person="element">
                    <span>{{ element.last }}, {{ element.first }}</span>
                  </PersonnelTooltip>
                </div>
              </template>
            </draggable>
            <span
              v-if="isDragging && zone.role === OverheadRole.IC && (personnelByRole.get(zone.role) ?? []).length === 0"
              class="absolute inset-0 flex items-center justify-center gap-1 text-[10px] font-mono uppercase tracking-widest text-primary/70 select-none pointer-events-none"
            ><span class="font-bold leading-none">★</span>IC</span>
          </div>
        </div>
      </div>

      <!-- Checked-out members (static, non-draggable) -->
      <div v-if="checkedOutMembers.length > 0" class="mt-2">
        <p class="text-[10px] font-mono text-outline uppercase tracking-widest mb-1">Checked Out</p>
        <div class="flex flex-wrap gap-1">
          <div
            v-for="m in checkedOutMembers"
            :key="m.personnel_id"
            class="flex items-center px-2 py-0.5 text-xs rounded-sm opacity-50 select-none cursor-default border"
            :class="isMatch(m) ? 'bg-primary/10 border-primary/60' : 'bg-surface-container border-border'"
          >
            <PersonnelTooltip :person="m">
              <span class="text-on-surface-variant">{{ m.last }}, {{ m.first }}</span>
            </PersonnelTooltip>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- IC assignment confirmation dialog -->
  <FosDialog
    :open="showIcConfirm"
    title="Assign Incident Commander"
    max-width="max-w-sm"
    @update:open="(val) => { if (!val) cancelIcAssignment() }"
  >
    <div class="space-y-3">
      <p class="text-sm text-on-surface-variant">
        Assign <strong class="text-on-surface">{{ pendingIcPerson?.last }}, {{ pendingIcPerson?.first }}</strong> as Incident Commander?
      </p>
      <div
        v-if="pendingExistingIcs.length > 0"
        class="flex items-start gap-2 text-sm text-amber-400 bg-amber-950/50 px-3 py-2.5"
      >
        <span class="material-symbols-outlined text-[16px] mt-0.5 shrink-0">warning</span>
        <span>
          <strong>{{ pendingExistingIcs.map((p) => `${p.last}, ${p.first}`).join(', ') }}</strong>
          {{ pendingExistingIcs.length === 1 ? 'is' : 'are' }} currently assigned as IC and will be moved to unassigned.
        </span>
      </div>
    </div>
    <template #footer>
      <FosButton variant="secondary" @click="cancelIcAssignment">Cancel</FosButton>
      <FosButton variant="primary" @click="confirmIcAssignment">Assign as IC</FosButton>
    </template>
  </FosDialog>
</template>
